<template>
  <div class="payment-validation-waiting">

    <!-- Waiting / polling state -->
    <div v-if="state === 'waiting'" class="py-3">

      <!-- Header: amount + method context -->
      <div class="text-center mb-3">
        <p class="mb-1 font-weight-bold">Select a matching payment</p>
        <p class="text-muted mb-0">
          Expected: <strong>{{ formatAmount(expectedAmount) }}</strong>
          <span v-if="paymentMethod"> &mdash; {{ paymentMethod }}</span>
        </p>
      </div>

      <!-- Unified phone / reference code input -->
      <div class="input-group input-group-sm mb-2">
        <input
          v-model="referenceCode"
          class="form-control"
          placeholder="Phone number or reference code"
          :disabled="looking"
          @keyup.enter="submitReference"
        />
        <div class="input-group-append">
          <button class="btn btn-primary" @click="submitReference" :disabled="looking || !referenceCode">
            <span v-if="looking"><i class="spinner-border spinner-border-sm"></i></span>
            <span v-else>Validate</span>
          </button>
        </div>
      </div>
      <small v-if="referenceError" class="text-danger d-block mb-2">{{ referenceError }}</small>

      <!-- Available matching transactions (narrowed by the input above, if any) -->
      <div v-if="visibleTransactions.length > 0" class="text-left">
        <table class="table table-hover table-sm mb-0">
          <tbody>
            <tr
              v-for="tx in visibleTransactions"
              :key="tx.id"
              @click="selectTransaction(tx)"
              style="cursor: pointer"
            >
              <td>
                <small class="text-muted">{{ tx.customer || (tx.sms_message && tx.sms_message.sender) || '—' }}</small>
              </td>
              <td><small class="font-weight-bold">{{ tx.transaction_code }}</small></td>
              <td><small>{{ formatAmount(tx.amount) }}</small></td>
              <td class="text-right"><i class="bi bi-check-circle-fill text-success"></i></td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Typed a full phone number, nothing has arrived for it yet -->
      <div v-else-if="showPushPrompt" class="text-center py-2">
        <p class="small text-muted mb-2">No matching payment found for that number yet.</p>
        <button class="btn btn-primary btn-sm" @click="requestPush" :disabled="pushSending">
          <span v-if="pushSending"><i class="spinner-border spinner-border-sm"></i></span>
          <span v-else>Send payment prompt to {{ referenceCode }}</span>
        </button>
        <small v-if="pushError" class="text-danger d-block mt-2">{{ pushError }}</small>
      </div>

      <!-- No matches yet -->
      <div v-else class="text-center text-muted py-2">
        <div class="spinner-border spinner-border-sm text-secondary mb-2" role="status">
          <span class="sr-only">Checking...</span>
        </div>
        <p class="small mb-0">No matching payments found yet — checking every 5 seconds</p>
      </div>

      <!-- Skip validation -->
      <div class="mt-2 text-center">
        <a
          href="#"
          :class="allowDirectSkip ? 'text-danger small' : 'text-warning small'"
          @click.prevent="state = allowDirectSkip ? 'direct_skip' : 'override'"
        >
          Skip validation
        </a>
      </div>
    </div>

    <!-- Supervisor approves the skip here, with their employee code and PIN -->
    <div v-else-if="state === 'override'" class="py-3">
      <p class="font-weight-bold text-warning">
        <i class="bi bi-person-exclamation"></i> Supervisor Approval Required
      </p>
      <p class="text-muted small mb-2">
        A supervisor enters their employee code and PIN below to approve skipping validation for this payment. It is logged with both names.
      </p>
      <div class="form-group">
        <textarea
          v-model="overrideReason"
          class="form-control"
          rows="3"
          placeholder="Reason why payment validation cannot be completed…"
          :disabled="submittingOverride"
        ></textarea>
      </div>
      <div class="form-row">
        <div class="form-group col-6">
          <input v-model="approverCode" class="form-control" placeholder="Supervisor employee code" autocomplete="off" :disabled="submittingOverride" />
        </div>
        <div class="form-group col-6">
          <input v-model="approverPin" type="password" inputmode="numeric" class="form-control" placeholder="PIN" autocomplete="new-password" :disabled="submittingOverride" />
        </div>
      </div>
      <div class="d-flex justify-content-between align-items-center">
        <a href="#" class="text-muted" @click.prevent="state = 'waiting'">Cancel</a>
        <button
          class="btn btn-primary"
          @click="submitSupervisorApproval"
          :disabled="submittingOverride || overrideReason.length < 10 || !approverCode || !approverPin"
        >
          <span v-if="submittingOverride"><i class="spinner-border spinner-border-sm"></i></span>
          <span v-else>Approve skip</span>
        </button>
      </div>
      <small v-if="overrideReason.length > 0 && overrideReason.length < 10" class="text-muted d-block mt-1">Reason: at least 10 characters.</small>
      <small v-if="overrideError" class="text-danger d-block mt-1">{{ overrideError }}</small>
    </div>

    <!-- Direct skip: company allows it, or the person at the till can approve skips -->
    <div v-else-if="state === 'direct_skip'" class="py-3">
      <p class="font-weight-bold text-danger">
        <i class="bi bi-skip-forward-fill"></i> Skip Payment Validation
      </p>
      <p class="text-muted small mb-2">
        This action is logged. Provide a reason before confirming.
      </p>
      <div class="form-group">
        <textarea
          v-model="overrideReason"
          class="form-control form-control-sm"
          rows="3"
          placeholder="Reason for skipping validation…"
          :disabled="submittingOverride"
        ></textarea>
      </div>
      <div class="d-flex justify-content-between align-items-center">
        <a href="#" class="text-muted" @click.prevent="state = 'waiting'">Cancel</a>
        <button
          class="btn btn-danger btn-sm"
          @click="submitDirectSkip"
          :disabled="submittingOverride || overrideReason.length < 5"
        >
          <span v-if="submittingOverride"><i class="spinner-border spinner-border-sm"></i></span>
          <span v-else>Confirm Skip</span>
        </button>
      </div>
      <small v-if="overrideError" class="text-danger d-block mt-1">{{ overrideError }}</small>
    </div>

    <!-- Validated -->
    <div v-else-if="state === 'validated'" class="text-center py-3">
      <div class="text-success mb-2">
        <i class="bi bi-check-circle-fill" style="font-size:2rem"></i>
      </div>
      <p class="font-weight-bold text-success mb-1">Payment Validated</p>
      <p class="text-muted">{{ validatedMessage }}</p>
    </div>


  </div>
</template>

<script>
import axios from 'axios';
import { mapGetters } from 'vuex';

export default {
  name: 'PaymentValidationWaiting',

  props: {
    expectedAmount:   { type: Number, required: true },
    recordType:       { type: String, required: true },
    recordId:         { type: String, required: true },
    paymentMethod:    { type: String, default: '' },
    paymentMethodId:  { type: String, default: null },
    // Only Dukakit currently has a push (STK) fallback to offer when a typed
    // phone number has no matching transaction yet.
    supportsPush:     { type: Boolean, default: false },
    pushSending:      { type: Boolean, default: false },
    pushError:        { type: String, default: '' },
  },

  data() {
    return {
      state: 'waiting', // waiting | override (supervisor approves here) | direct_skip | validated
      matchedTransactions: [],
      referenceCode: '',
      referenceError: '',
      looking: false,
      overrideReason: '',
      approverCode: '',
      approverPin: '',
      overrideError: '',
      submittingOverride: false,
      validatedMessage: '',
      pollingInterval: null,
    };
  },

  computed: {
    ...mapGetters(['userDetails']),

    // Same rule as PaymentValidationService::canSkipValidation.
    canApproveSkip() {
      const roles = this.userDetails?.roles || [];
      const perms = this.userDetails?.permissions || [];
      return roles.some(r => r.name === 'Admin' || r.name === 'owner')
        || perms.some(p => p.name === 'skip_payment_validation' || p.name === 'can manage orders');
    },

    // Skip straight away (reason only) when the company allows everyone to, or
    // when the person at the till could approve the skip themselves.
    allowDirectSkip() {
      return !!(this.userDetails?.company?.companysettings?.allow_all_employees_skip_validation) || this.canApproveSkip;
    },

    // Narrows the polled (amount-matched) list by whatever's typed in the
    // phone/reference box, so the cashier doesn't need a separate search step.
    visibleTransactions() {
      const q = this.referenceCode.trim().toLowerCase();
      if (!q) return this.matchedTransactions;
      return this.matchedTransactions.filter((tx) => {
        const haystack = [
          tx.customer,
          tx.transaction_code,
          tx.account_number,
          tx.phone,
          tx.sms_message && tx.sms_message.sender,
        ].filter(Boolean).join(' ').toLowerCase();
        return haystack.includes(q);
      });
    },

    looksLikePhone() {
      return this.referenceCode.replace(/\D/g, '').length >= 9;
    },

    showPushPrompt() {
      return this.supportsPush && this.looksLikePhone && this.visibleTransactions.length === 0;
    },
  },

  mounted() {
    this.startPolling();
  },

  beforeDestroy() {
    this.stopPolling();
  },

  methods: {
    startPolling() {
      this.poll();
      this.pollingInterval = setInterval(this.poll, 5000);
    },

    stopPolling() {
      if (this.pollingInterval) {
        clearInterval(this.pollingInterval);
        this.pollingInterval = null;
      }
    },

    async poll() {
      if (this.state !== 'waiting') return;
      try {
        const params = { amount: this.expectedAmount };
        if (this.paymentMethodId) params.payment_method_id = this.paymentMethodId;

        const { data } = await axios.get('/api/payment-validation/available-transactions', { params });

        if (Array.isArray(data.transactions) && data.transactions.length > 0) {
          this.matchedTransactions = data.transactions;
        }
      } catch {
        // silent — polling must not surface errors to the cashier
      }
    },

    async selectTransaction(tx) {
      this.stopPolling();
      try {
        const { data } = await axios.post('/api/payment-validation/validate-by-transaction', {
          transactions_validated_id: tx.id,
          record_type:               this.recordType,
          record_id:                 this.recordId,
        });
        if (data.validated) {
          this.state           = 'validated';
          this.validatedMessage = `Confirmed: ${tx.transaction_code}`;
          this.$emit('validated', data);
        }
      } catch {
        this.startPolling();
      }
    },

    // Nothing has arrived for the typed number yet - hand off to the parent,
    // which knows how to trigger an STK push for whichever gateway is active.
    requestPush() {
      this.$emit('request-push', { phone: this.referenceCode.trim() });
    },

    async submitReference() {
      if (this.recordType === 'order') {
        return this.submitOrderReference();
      }

      this.referenceError = '';
      this.looking = true;
      try {
        const { data } = await axios.post('/api/payment-validation/validate-by-reference', {
          reference:   this.referenceCode,
          record_type: this.recordType,
          record_id:   this.recordId,
        });
        if (data.validated) {
          this.stopPolling();
          this.state           = 'validated';
          this.validatedMessage = `Confirmed: ${data.transaction?.transaction_code || this.referenceCode}`;
          this.$emit('validated', data);
        }
      } catch (e) {
        this.referenceError = e.response?.data?.message || 'No matching transaction found for that reference.';
      } finally {
        this.looking = false;
      }
    },

    // Order checkout: preview the reference code's real remaining amount
    // (it may already be partly allocated from a backoffice split) before
    // committing, so any excess over the order balance can be routed to a
    // wallet instead of being silently dropped or mis-applied.
    async submitOrderReference() {
      this.referenceError = '';
      this.looking = true;
      try {
        const { data: preview } = await axios.post('/api/payment-validation/preview-order-reference', {
          reference: this.referenceCode,
          order_id:  this.recordId,
        });

        const excess = preview.unallocated - preview.order_balance;

        if (excess > 0.01) {
          this.stopPolling();
          this.$emit('excess-detected', {
            transactions_validated_id: preview.transactions_validated_id,
            transaction_code:          preview.transaction_code,
            order_balance:             preview.order_balance,
            unallocated:               preview.unallocated,
            excess,
          });
          return;
        }

        const appliedAmount = Math.min(preview.unallocated, preview.order_balance);

        const { data } = await axios.post('/api/payment-validation/validate-by-transaction', {
          transactions_validated_id: preview.transactions_validated_id,
          record_type:               this.recordType,
          record_id:                 this.recordId,
        });

        if (data.validated) {
          this.stopPolling();
          this.state           = 'validated';
          this.validatedMessage = `Confirmed: ${preview.transaction_code}`;
          this.$emit('validated', { ...data, applied_amount: appliedAmount });
        }
      } catch (e) {
        this.referenceError = e.response?.data?.message || 'No matching transaction found for that reference.';
      } finally {
        this.looking = false;
      }
    },

    async submitSupervisorApproval() {
      this.overrideError      = '';
      this.submittingOverride = true;
      try {
        const { data } = await axios.post('/api/payment-validation/approve-skip', {
          record_type:     this.recordType,
          record_id:       this.recordId,
          reason:          this.overrideReason,
          payment_method:  this.paymentMethod,
          expected_amount: this.expectedAmount,
          employee_code:   this.approverCode,
          employee_pin:    this.approverPin,
        });
        this.stopPolling();
        this.state            = 'validated';
        this.validatedMessage = `Skip approved by ${data.approved_by}.`;
        this.$emit('validated', { skipped: true });
      } catch (e) {
        this.overrideError = e.response?.status === 429
          ? 'Too many attempts. Wait a minute and try again.'
          : (e.response?.data?.message || 'Approval failed.');
      } finally {
        this.approverPin        = '';
        this.submittingOverride = false;
      }
    },

    async submitDirectSkip() {
      this.overrideError      = '';
      this.submittingOverride = true;
      try {
        await axios.post('/api/payment-validation/direct-skip', {
          record_type: this.recordType,
          record_id:   this.recordId,
          reason:      this.overrideReason,
        });
        this.stopPolling();
        this.state            = 'validated';
        this.validatedMessage = 'Validation skipped.';
        this.$emit('validated', { skipped: true });
      } catch (e) {
        this.overrideError = e.response?.data?.message || 'Failed to skip validation.';
      } finally {
        this.submittingOverride = false;
      }
    },

    formatAmount(amount) {
      if (this.$root?.convertCurrencyToLocal) {
        return this.$root.convertCurrencyToLocal(amount);
      }
      return amount ? Number(amount).toFixed(2) : '0.00';
    },
  },
};
</script>
